import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, ArrowLeft, Ticket, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CouponManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [stats, setStats] = useState({
    total_coupons: 0,
    active_coupons: 0,
    total_discount_given: 0
  });
  
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
      
      const activeCoupons = response.data.filter(c => c.is_active).length;
      setStats({
        total_coupons: response.data.length,
        active_coupons: activeCoupons,
        total_discount_given: 0
      });
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    if (!couponForm.code || !couponForm.discount_value) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingCoupon) {
        // Update existing coupon
        await axios.put(
          `${API}/admin/coupons/${editingCoupon.id}`,
          {
            ...couponForm,
            discount_value: parseFloat(couponForm.discount_value)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon updated successfully');
      } else {
        // Create new coupon
        await axios.post(
          `${API}/admin/coupons`,
          {
            ...couponForm,
            discount_value: parseFloat(couponForm.discount_value)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon created successfully');
      }
      setShowDialog(false);
      setEditingCoupon(null);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '' });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingCoupon ? 'Failed to update coupon' : 'Failed to create coupon'));
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString()
    });
    setShowDialog(true);
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/super-admin')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Coupon Management</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Ticket className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Coupons</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_coupons}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Ticket className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Active Coupons</p>
            <p className="text-3xl font-bold text-gray-900">{stats.active_coupons}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Ticket className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Discount Given</p>
            <p className="text-3xl font-bold text-gray-900">₹{stats.total_discount_given}</p>
          </div>
        </div>

        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">All Coupons</h2>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) {
              setEditingCoupon(null);
              setCouponForm({ code: '', discount_type: 'percentage', discount_value: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button style={{backgroundColor: 'var(--primary)', color: 'white'}} className="hover:opacity-90">
                <Plus className="h-4 w-4 mr-2" />
                Create Coupon
              </Button>
            </DialogTrigger>
            <DialogContent style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
              <DialogHeader>
                <DialogTitle style={{color: 'var(--foreground)'}}>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCoupon} className="space-y-4">
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Coupon Code *</Label>
                  <Input
                    value={couponForm.code}
                    onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., SAVE20"
                    required
                    disabled={editingCoupon !== null}
                    className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                    style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                  />
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Discount Type *</Label>
                  <Select value={couponForm.discount_type} onValueChange={(value) => setCouponForm({...couponForm, discount_type: value})}>
                    <SelectTrigger style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Discount Value *</Label>
                  <Input
                    type="number"
                    value={couponForm.discount_value}
                    onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                    placeholder={couponForm.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                    required
                    className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                    style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                  />
                </div>
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowDialog(false);
                      setEditingCoupon(null);
                      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 hover:opacity-90" style={{backgroundColor: 'var(--primary)', color: 'white'}}>
                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">Loading...</div>
          ) : coupons.length === 0 ? (
            <div className="p-12 text-center">
              <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No coupons created yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Coupon Code</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Created</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td className="px-6 py-4 text-sm font-bold" style={{color: '#A89968'}}>{coupon.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 capitalize">{coupon.discount_type}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                      </td>
                      <td className="px-6 py-4">
                        {coupon.is_active ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{backgroundColor: '#E8E4C9', color: '#A89968'}}>
                            Active
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(coupon.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCoupon(coupon)}
                            style={{color: 'var(--accent)'}}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponManagement;
